import React, { useEffect, useState } from 'react'
import { EyebrowSM, TitleXS } from '@/atoms/Text'
import { FontWeight } from '@/atoms/Text/Text'
import { NamedColor } from '@/constants/types'
import { CollectibleProductResponse, PaymentIntent } from '@/services/NftService/types'
import { formatPrice } from '@/services/PriceFormatter'
import { useTranslate } from '@/utils/translate/translate-client'

export interface PackOrderSummaryProps {
  loading: boolean
  paymentIntentData?: CollectibleProductResponse
}

const getPurchaseSectionText = (purchaseIntent?: PaymentIntent) => {
  if (!purchaseIntent)
    return {
      price: '$--.--',
      tax: '$--.--',
      subtotal: '$--.--',
      chargeAmount: '$--.--',
      accountCredit: '$--.--',
    }
  const price = formatPrice(purchaseIntent?.price || 0, { currency: 'USD' })
  const tax = formatPrice(purchaseIntent?.tax || 0, { currency: 'USD' })
  const subtotal = formatPrice((purchaseIntent?.price || 0) + (purchaseIntent?.tax || 0), { currency: 'USD' })
  const chargeAmount = formatPrice(purchaseIntent?.chargeAmount || 0, { currency: 'USD' })
  const accountCredit = formatPrice(
    (purchaseIntent?.price || 0) + (purchaseIntent?.tax || 0) - (purchaseIntent?.chargeAmount || 0),
    { currency: 'USD' },
  )

  return { price, tax, subtotal, chargeAmount, accountCredit }
  return { price, tax, subtotal, chargeAmount }
}

interface OrderSummaryLineProps {
  loading?: boolean
  price: string
  title: string
  titleWeight?: FontWeight
  priceWeight?: FontWeight
  titleColor?: NamedColor
  priceColor?: NamedColor
}

const OrderSummaryLine: React.FC<OrderSummaryLineProps> = ({
  title,
  loading,
  price,
  titleWeight = 'medium',
  priceWeight = 'medium',
  priceColor = 'black',
  titleColor = 'black',
}) => {
  return (
    <div className="flex flex-row justify-between border-b border-b-core-gray-300 py-3 md:px-0">
      <TitleXS weight={titleWeight} color={titleColor}>
        {title}:
      </TitleXS>
      <div className="font-semibold ">
        {loading ? (
          <div className="animate-pulse w-[50px] h-[18.66667px] bg-core-gray-500 rounded-lg" />
        ) : (
          <TitleXS weight={priceWeight} color={priceColor}>
            {price}
          </TitleXS>
        )}
      </div>
    </div>
  )
}

const PackOrderSummary: React.FC<PackOrderSummaryProps> = ({ loading, paymentIntentData }) => {
  const { t } = useTranslate('account')
  const [price, setPrice] = useState('$--.--')
  const [tax, setTax] = useState('$--.--')
  const [subtotal, setSubtotal] = useState('$--.--')
  const [chargeAmount, setChargeAmount] = useState('$--.--')
  const [accountCredit, setAccountCredit] = useState('$--.--')

  useEffect(() => {
    const texts = getPurchaseSectionText(paymentIntentData?.node)
    setPrice(texts.price)
    setTax(texts.tax)
    setSubtotal(texts.subtotal)
    setChargeAmount(texts.chargeAmount)
    setAccountCredit(texts.accountCredit ?? '')
  }, [paymentIntentData])

  return (
    <div className="flex flex-col text-core-gray-700">
      <EyebrowSM>{t('packOrderSummary', 'Order Summary')}</EyebrowSM>
      <OrderSummaryLine
        loading={loading}
        price={price}
        title={t('collectiblePackPrice', 'Collectible Price')}
        priceWeight="semibold"
      />
      <OrderSummaryLine
        loading={loading}
        price={tax}
        title={t('estimatedPackTaxes', 'Estimated Taxes')}
        priceColor="core-gray-600"
      />
      <OrderSummaryLine
        loading={loading}
        price={subtotal}
        title={t('packSubTotal', 'Subtotal')}
        priceColor="core-gray-600"
      />
      {/* Hard-coded a negative to this line, the values are formatted on the backend, another get it out the door compromise. */}
      <OrderSummaryLine
        loading={loading}
        price={`-${accountCredit}`}
        title={t('usingAccountCredit', 'Using Account Credit')}
        priceColor="core-gray-600"
      />
      <OrderSummaryLine
        loading={loading}
        price={chargeAmount}
        title={t('remainingBalance', 'Remaining Balance')}
        priceWeight="bold"
        titleWeight="bold"
      />
    </div>
  )
}

export default PackOrderSummary
